import usePrivateAxios from '@/common/api/usePrivateAxios'
import { ErrorResponse } from '@/common/types/errorResponse'
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters'
import { useMutation } from '@tanstack/react-query'
import {
  GovernmentAllocationEntryProductCategoryCurrencyValidationSchema,
  GovernmentAllocationEntryProductCategoryQuantityValidationSchema,
} from '../pages/CompanyGovernmentAllocations/schemas/update.schema'

type FormProductCategory =
  | GovernmentAllocationEntryProductCategoryCurrencyValidationSchema
  | GovernmentAllocationEntryProductCategoryQuantityValidationSchema

const isCurrencyForm = (
  form: FormProductCategory,
): form is GovernmentAllocationEntryProductCategoryCurrencyValidationSchema => {
  return 'total_value' in form
}

type UseCreateGovernmentAllocationEntryProductCategoryProps = {
  governmentAllocationId?: string
  onSuccess: () => void
  onError: (error: ErrorResponse) => void
}

export const useCreateGovernmentAllocationEntryProductCategory = ({
  governmentAllocationId,
  onSuccess,
  onError,
}: UseCreateGovernmentAllocationEntryProductCategoryProps) => {
  const privateAxios = usePrivateAxios()

  return useMutation({
    mutationKey: ['createGovernmentAllocationEntryProductCategory', governmentAllocationId],
    mutationFn: async (form: FormProductCategory) => {
      let government_allocation_entry

      if (isCurrencyForm(form)) {
        government_allocation_entry = {
          entry_product_categories_attributes: [
            {
              product_category_id: form.product_category.id,
              total_value: form.total_value ? unFormatCurrency(form.total_value) : null,
              entry_products_attributes: form.entry_products.map((entryProduct) => ({
                product_id: entryProduct.product.id,
                total_value: entryProduct.total_value ? unFormatCurrency(entryProduct.total_value) : null,
              })),
            },
          ],
        }
      } else {
        government_allocation_entry = {
          entry_product_categories_attributes: [
            {
              product_category_id: form.product_category.id,
              total_quantity: form.total_quantity ? unFormatUnknownUnit(form.total_quantity) : null,
              entry_products_attributes: form.entry_products.map((entryProduct) => ({
                product_id: entryProduct.product.id,
                total_quantity: entryProduct.total_quantity ? unFormatUnknownUnit(entryProduct.total_quantity) : null,
              })),
            },
          ],
        }
      }

      return privateAxios.put(`/billing/government_allocation_entries/${governmentAllocationId}`, {
        government_allocation_entry,
      })
    },
    onSuccess,
    onError,
  })
}
